import Draggable from './Draggable';
import StateSubscriber from '../../event/StateSubscriber';
import { getStore, getState } from '../../store';
import {
  DRAG_STATE,
  DOMAINS,
  DOMAIN_PROPERTY_DRAG_STATE,
  EVENT_DRAGENTER,
  EVENT_DRAGLEAVE,
  EVENT_DROP,
} from '../../dragState/constants';
import DragState from '../../dragState/constants/DragState';
import * as pointerPosStore from './pointerPosStore';
import { selectDomainData } from '../../dragState/selectors';
import Subscription from './Subscription';
import HitAreaManager from './HitAreaManager';
import setDifference from './setDifference';
import { gtmTrackComponentToolDrag } from '../../../utils/gtm/handler';

class DragStateServices {
  constructor(id = null, context = null, iframeElToListenTo = null) {
    // USED BY PLUGIN
    this.id = id;
    this.context = context;
    this.iframeElToListenTo = iframeElToListenTo;
    if (id) {
      // We're being initialised by plugin provider, not frontend.
      // If this was frontend, it would be too early to call `getStore()`.
      this.stateSubscriber = new StateSubscriber(getStore());
    }
    this.subscriptions = {}; // domainName -> subscription
    this.hitAreaManagers = {}; // domainName -> hitAreaManager
    this.triggerEventOnDroppables = {}; // domainName -> Function
    this.lastHits = {}; // domainName -> Set<String (droppableId)>
    this.isDragging = {}; // domainName -> Boolean
    this.pendingDragDataByDomain = {};
    // USED BY FRONTEND
    this.draggables = [];
  }

  //
  // USED BY PLUGIN
  //
  // PUBLIC

  subscribeToDraggables(domainName, callbackDragStarted, callbackDragStopped) {
    if (!this.hitAreaManagers[domainName]) {
      this.hitAreaManagers[domainName] = new HitAreaManager(this.iframeElToListenTo);
    }

    if (!this.subscriptions[domainName]) {
      const unsubscribe = this.stateSubscriber.subscribe(
        (dragState) => {
          switch (dragState) {
            case DragState.DRAGGING:
              this.dragStarted(domainName, callbackDragStarted);
              break;
            case DragState.NOT_DRAGGING:
              this.dragStopped(domainName, callbackDragStopped);
              break;
            default:
              break;
          }
        },
        [DRAG_STATE, DOMAINS, domainName, DOMAIN_PROPERTY_DRAG_STATE],
      );
      const subscription = new Subscription(unsubscribe);
      this.subscriptions[domainName] = subscription;
    }
  }

  dragStarted(domainName, callbackDragStarted) {
    if (this.isDragging[domainName] || !this.subscriptions[domainName]) {
      return false;
    }
    this.lastHits[domainName] = new Set();
    this.isDragging[domainName] = true;

    pointerPosStore.subscribe(domainName, (lastPointerPos) => {
      const hits = this.hitAreaManagers[domainName].hitTest(lastPointerPos);

      const entered = setDifference(hits, this.lastHits[domainName]);
      const left = setDifference(this.lastHits[domainName], hits);

      if (entered.size > 0) {
        this.triggerEventOnDroppables[domainName](EVENT_DRAGENTER, [...entered]);
      }
      if (left.size > 0) {
        this.triggerEventOnDroppables[domainName](EVENT_DRAGLEAVE, [...left]);
      }

      this.lastHits[domainName] = hits;
    });

    const domainData = selectDomainData(getState(), domainName);
    if (domainData?.dragData) {
      this.pendingDragDataByDomain[domainName] = domainData.dragData;
    }

    return callbackDragStarted(domainData).then((callbackDragStartedResponse) => {
      const { hitAreas, triggerEventOnDroppables } = callbackDragStartedResponse;

      this.hitAreaManagers[domainName].setHitAreas(hitAreas);
      this.triggerEventOnDroppables[domainName] = triggerEventOnDroppables;
    });
  }

  dragStopped(domainName, callbackDragStopped) {
    if (!this.isDragging[domainName]) {
      return false;
    }

    pointerPosStore.unsubscribeAll(domainName);

    if (this.lastHits[domainName].size > 0) {
      gtmTrackComponentToolDrag(this.pendingDragDataByDomain[domainName]);
      this.triggerEventOnDroppables[domainName](EVENT_DROP, [...this.lastHits[domainName]]);
    }
    delete this.pendingDragDataByDomain[domainName];
    this.lastHits[domainName] = new Set();
    this.isDragging[domainName] = false;

    return callbackDragStopped().then(() => {
      this.hitAreaManagers[domainName].removeHitAreas();
    });
  }

  // PUBLIC
  unsubscribeFromDraggables(domainName) {
    if (this.hitAreaManagers[domainName]) {
      this.hitAreaManagers[domainName].destroy();
      delete this.hitAreaManagers[domainName];
    }

    if (this.subscriptions[domainName]) {
      this.subscriptions[domainName].unsubscribe();
      delete this.subscriptions[domainName];
    }
  }

  //
  // USED BY FRONTEND
  //
  // PUBLIC
  addDraggable(el, domainName, dragType, dragData, dragPreviewEl) {
    const existingDraggable = this.findInDraggables(el);
    if (existingDraggable) {
      return;
    }
    const draggable = new Draggable(el, domainName, dragType, dragData, dragPreviewEl);
    draggable.startListening();

    this.draggables.push(draggable);
  }

  // PUBLIC
  removeDraggable(el) {
    const draggable = this.findInDraggables(el);
    if (draggable) {
      draggable.stopListening();
      this.deleteInDraggables(draggable);
    }
  }

  findInDraggables(el) {
    for (const draggable of this.draggables) {
      if (draggable.el === el) {
        return draggable;
      }
    }
    return false;
  }

  deleteInDraggables(draggable) {
    const index = this.draggables.indexOf(draggable);
    if (index !== -1) {
      this.draggables.splice(index, 1);
    }
  }

  static build(args) {
    return new DragStateServices(args.id, args.context, args.iframeElToListenTo);
  }
}

export default DragStateServices;
